/**
 * Fonts offered by the place card designer. Families not already loaded globally (index.html) are
 * fetched on demand so the canvas preview and the PDF render with the same typeface.
 */

import type { PlaceCardElement } from '@/types';

export const PLACE_CARD_FONT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Times New Roman, Times, serif', label: 'Times New Roman' },
  { value: 'EB Garamond, Garamond, serif', label: 'Garamond' },
  { value: 'Warnock Pro, Garamond, serif', label: 'Warnock Pro' },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif', label: 'Inter' },
  { value: 'Montserrat, Arial, sans-serif', label: 'Montserrat' },
  { value: 'Poppins, Arial, sans-serif', label: 'Poppins' },
  { value: 'Courier New, Courier, monospace', label: 'Courier New' },
  { value: 'Great Vibes, cursive', label: 'Great Vibes' },
  { value: 'Dancing Script, cursive', label: 'Dancing Script' },
  { value: 'Parisienne, cursive', label: 'Parisienne' },
];

const ON_DEMAND_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,700&family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;1,400;1,700&family=Poppins:ital,wght@0,400;0,500;0,600;0,700;1,400;1,700&display=swap';

function ensureOnDemandFontStylesheet(): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }
  const existing = document.querySelector<HTMLLinkElement>('link[data-place-card-fonts]');
  if (existing) {
    return existing.dataset.loaded === 'true'
      ? Promise.resolve()
      : new Promise((resolve) => {
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', () => resolve(), { once: true });
        });
  }
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = ON_DEMAND_FONTS_HREF;
    link.dataset.placeCardFonts = 'true';
    link.addEventListener(
      'load',
      () => {
        link.dataset.loaded = 'true';
        resolve();
      },
      { once: true }
    );
    link.addEventListener('error', () => resolve(), { once: true });
    document.head.appendChild(link);
  });
}

/** First family in a CSS font-family list, unquoted. */
function primaryFamily(fontFamily: string): string {
  return fontFamily.split(',')[0].trim().replace(/^['"]|['"]$/g, '');
}

/**
 * Loads the font files used by the given elements (all weights/styles they use). Resolves once
 * they are available or have failed, so callers can render text with the final metrics.
 */
export async function ensurePlaceCardFontsLoaded(elements: PlaceCardElement[]): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) {
    return;
  }
  await ensureOnDemandFontStylesheet();

  const faces = new Set<string>();
  elements.forEach((element) => {
    if (element.type !== 'text') {
      return;
    }
    const family = primaryFamily(element.fontFamily ?? 'Georgia, serif');
    const weight = cssFontWeight(element.fontWeight);
    const style = element.fontStyle === 'italic' ? 'italic' : 'normal';
    faces.add(`${style} ${weight} 16px "${family}"`);
  });

  await Promise.all(
    Array.from(faces).map((face) => document.fonts.load(face).catch(() => undefined))
  );
}

export function cssFontWeight(weight: PlaceCardElement['fontWeight']): string {
  switch (weight) {
    case 'medium':
      return '500';
    case 'semibold':
      return '600';
    case 'bold':
      return '700';
    default:
      return '400';
  }
}
